import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Loader2, FileText, Tag, X, Star } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SmoothScroll from "@/components/landing/SmoothScroll";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

export default function Articles() {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("kategori") || "";
  const activeTag = params.get("tag") || "";

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], tags: [] });
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/articles/meta`).then((r) => setMeta(r.data || { categories: [], tags: [] })).catch(() => {});
    axios.get(`${API}/articles/featured`).then((r) => setFeatured(r.data || null)).catch(() => setFeatured(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (activeCategory) q.category = activeCategory;
    if (activeTag) q.tag = activeTag;
    axios.get(`${API}/articles`, { params: q })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [activeCategory, activeTag]);

  const setFilter = (kind, name) => {
    const next = new URLSearchParams(params);
    if (!name) next.delete(kind);
    else next.set(kind, name);
    // clear the other filter when switching kind
    if (kind === "kategori") next.delete("tag");
    if (kind === "tag") next.delete("kategori");
    setParams(next, { replace: true });
    if (typeof window !== "undefined") window.scrollTo({ top: 240, behavior: "smooth" });
  };

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  const hasFilter = !!(activeCategory || activeTag);
  const showFeaturedBanner = featured && !hasFilter;
  const displayRows = showFeaturedBanner
    ? rows.filter((r) => r.id !== featured.id)
    : rows;

  const activeLabel = useMemo(() => {
    if (activeCategory) return `Kategori: ${activeCategory}`;
    if (activeTag) return `Tag: #${activeTag}`;
    return "";
  }, [activeCategory, activeTag]);

  return (
    <main className="bg-parchment text-forest min-h-screen">
      <Seo
        title={
          activeCategory ? `Artikel Kategori "${activeCategory}"`
          : activeTag ? `Artikel Tag "#${activeTag}"`
          : "Artikel Pondok"
        }
        description="Kumpulan tulisan seputar tahfizh, tarbiyah, dan kehidupan santri di Pondok Tahfizh Generasi Rabbani Qurani. Kisah, kajian, & kabar dari rumah para penghafal."
      />
      <SmoothScroll />
      <Navbar />

      {/* Hero band */}
      <section className="relative pt-40 pb-14 md:pt-48 md:pb-20 border-b border-gold/25" data-testid="articles-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-xs tracking-[0.45em] uppercase text-gold"
          >
            Artikel Pondok
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05 }}
            className="mt-4 font-serif-display text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl"
          >
            Kisah, kajian, &amp; kabar<br />
            <span className="italic text-gold">dari rumah para penghafal.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-6 max-w-2xl text-moss leading-relaxed"
          >
            Kumpulan tulisan seputar tahfizh, tarbiyah, dan kehidupan santri di Pondok Tahfizh Generasi Rabbani Qurani.
          </motion.p>
        </div>
      </section>

      {/* Featured banner (only when no active filter) */}
      {showFeaturedBanner && (
        <FeaturedBanner article={featured} />
      )}

      {/* Filter bar */}
      {(meta.categories.length > 0 || meta.tags.length > 0) && (
        <section className="py-8 border-b border-gold/20 bg-white/40" data-testid="articles-filter-bar">
          <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-4">
            {meta.categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-moss mr-1">Kategori</span>
                <button
                  onClick={() => setFilter("kategori", "")}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                    !activeCategory ? "bg-forest text-parchment" : "bg-sand text-forest hover:bg-gold/20"
                  }`}
                  data-testid="filter-kategori-all"
                >
                  Semua
                </button>
                {meta.categories.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setFilter("kategori", c.name)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors ${
                      activeCategory === c.name
                        ? "bg-forest text-parchment"
                        : "bg-sand text-forest hover:bg-gold/20"
                    }`}
                    data-testid={`filter-kategori-${c.name}`}
                  >
                    {c.name}
                    <span className="opacity-70">·{c.count}</span>
                  </button>
                ))}
              </div>
            )}
            {meta.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-moss mr-1">Tag</span>
                {meta.tags.slice(0, 15).map((t) => (
                  <button
                    key={t.name}
                    onClick={() => setFilter("tag", t.name)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                      activeTag.toLowerCase() === t.name.toLowerCase()
                        ? "bg-gold text-forest border-gold"
                        : "bg-transparent text-moss border-gold/30 hover:bg-gold/10 hover:text-forest"
                    }`}
                    data-testid={`filter-tag-${t.name}`}
                  >
                    #{t.name}<span className="opacity-70">·{t.count}</span>
                  </button>
                ))}
              </div>
            )}
            {activeLabel && (
              <div className="flex items-center gap-2 pt-1" data-testid="active-filter-indicator">
                <span className="text-xs text-forest">
                  Menampilkan <strong>{activeLabel}</strong>
                </span>
                <button
                  onClick={clearAll}
                  className="inline-flex items-center gap-1 text-[11px] text-red-700 hover:underline"
                  data-testid="filter-clear"
                >
                  <X size={11} /> Hapus filter
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* List */}
      <section className="py-14 md:py-20" data-testid="articles-list-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-24 text-moss text-sm">
              <Loader2 className="animate-spin mr-2" size={16} /> Memuat artikel…
            </div>
          ) : displayRows.length === 0 ? (
            !showFeaturedBanner ? (
              <EmptyState hasFilter={!!activeLabel} onClear={clearAll} />
            ) : null
          ) : (
            <>
              {showFeaturedBanner && (
                <div className="mb-8 flex items-center gap-3">
                  <span className="text-[10px] tracking-[0.4em] uppercase text-gold">Artikel Lainnya</span>
                  <span className="flex-1 h-px bg-gold/20" />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-grid">
                {displayRows.map((a, i) => (
                  <ArticleCard key={a.id} article={a} index={i} onTag={(t) => setFilter("tag", t)} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

function ArticleCard({ article, index, onTag }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08 }}
      className="group flex flex-col rounded-2xl overflow-hidden border border-gold/30 bg-white/60 hover:border-gold/60 transition-colors"
      data-testid={`article-card-${article.slug}`}
    >
      <Link to={`/artikel/${article.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-sand">
        {article.cover_image ? (
          <img
            src={article.cover_image}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-moss/60">
            <FileText size={32} />
          </div>
        )}
        {article.category && (
          <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-forest/85 text-parchment text-[10px] tracking-[0.15em] uppercase">
            {article.category}
          </span>
        )}
      </Link>
      <div className="flex-1 p-6 flex flex-col">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gold">
          {formatDate(article.published_at || article.created_at)}
          {article.author ? ` · ${article.author}` : ""}
        </p>
        <h3 className="mt-2 font-serif-display text-forest text-2xl leading-tight">
          <Link to={`/artikel/${article.slug}`} className="hover:text-gold transition-colors" data-testid={`article-title-link-${article.slug}`}>
            {article.title}
          </Link>
        </h3>
        {article.excerpt && (
          <p className="mt-3 text-sm text-moss leading-relaxed line-clamp-3">{article.excerpt}</p>
        )}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {article.tags.slice(0, 4).map((t) => (
              <button
                key={t}
                onClick={(e) => { e.preventDefault(); onTag && onTag(t); }}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sand text-forest border border-gold/30 hover:bg-gold/20"
              >
                <Tag size={9} /> {t}
              </button>
            ))}
          </div>
        )}
        <div className="mt-6 pt-4 border-t border-gold/20">
          <Link
            to={`/artikel/${article.slug}`}
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-forest hover:text-gold transition-colors"
            data-testid={`article-read-more-${article.slug}`}
          >
            Baca artikel <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

function FeaturedBanner({ article }) {
  return (
    <section
      className="relative py-14 md:py-20 border-b border-gold/25 overflow-hidden"
      data-testid="featured-banner"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-forest/5 via-transparent to-gold/5 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="mb-6 flex items-center gap-3">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold text-forest text-[10px] tracking-[0.35em] uppercase font-semibold">
            <Star size={12} fill="currentColor" /> Artikel Unggulan
          </span>
          <span className="flex-1 h-px bg-gold/25" />
        </div>

        <motion.article
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="group grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center"
          data-testid={`featured-article-${article.slug}`}
        >
          {/* Cover */}
          <Link
            to={`/artikel/${article.slug}`}
            className="lg:col-span-3 block relative aspect-[16/10] lg:aspect-[16/11] rounded-2xl overflow-hidden border border-gold/40 bg-sand shadow-xl"
          >
            {article.cover_image ? (
              <img
                src={article.cover_image}
                alt={article.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                loading="eager"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-moss/60">
                <FileText size={48} />
              </div>
            )}
            {article.category && (
              <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-forest/85 text-parchment text-[10px] tracking-[0.15em] uppercase">
                {article.category}
              </span>
            )}
          </Link>

          {/* Content */}
          <div className="lg:col-span-2">
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold">
              {formatDate(article.published_at || article.created_at)}
              {article.author ? ` · ${article.author}` : ""}
            </p>
            <h2 className="mt-3 font-serif-display text-forest text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
              <Link
                to={`/artikel/${article.slug}`}
                className="hover:text-gold transition-colors"
                data-testid="featured-title-link"
              >
                {article.title}
              </Link>
            </h2>
            {article.excerpt && (
              <p className="mt-5 text-base md:text-lg text-moss leading-relaxed line-clamp-4">
                {article.excerpt}
              </p>
            )}
            <div className="mt-8">
              <Link
                to={`/artikel/${article.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest text-parchment text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors"
                data-testid="featured-cta"
              >
                Baca artikel <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
    </section>
  );
}


function EmptyState({ hasFilter, onClear }) {
  return (
    <div className="py-24 text-center" data-testid="articles-empty">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sand border border-gold/40 text-forest">
        <FileText size={22} />
      </div>
      <h3 className="mt-6 font-serif-display text-forest text-2xl">
        {hasFilter ? "Tidak ada artikel yang cocok" : "Belum ada artikel"}
      </h3>
      <p className="mt-2 text-moss text-sm max-w-md mx-auto">
        {hasFilter
          ? "Coba pilih kategori/tag lain, atau lihat semua artikel."
          : "Artikel akan tampil di sini segera setelah admin menerbitkannya."}
      </p>
      {hasFilter && (
        <button
          onClick={onClear}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest text-parchment text-xs hover:bg-gold"
          data-testid="empty-clear-filter"
        >
          Lihat semua artikel
        </button>
      )}
    </div>
  );
}
